import { useTextes } from '@common/config';
import { CampagneStatut, OrganismeFichierType, OrganismeStatutType, useDownloadOrganismeFichierFileMutation, useRegisterOrganismeMutation, useUpdateOrganismeRegistrationMutation } from '@common/services';
import { SavedFile } from '@common/ui';
import { appToast } from '@common/ui/toast';
import { fetchLocalFile, jsonToBlob } from '@common/utils';
import { useNavigate } from 'react-router-dom';
import { useInscriptionState } from '../../../hooks/use-inscription-state.hook';
import { OrganismeInscriptionData } from '../../stepper/types/organisme-inscription-data.type';

export function useSummaryActions() {
  const t = useTextes('Commun', 'Representants');
  const navigate = useNavigate();
  const inscription = useInscriptionState();

  const [createInscription, { isLoading: isInscriptionLoading }] = useRegisterOrganismeMutation();
  const [updateInscription, { isLoading: isUpdateInscriptionLoading }] = useUpdateOrganismeRegistrationMutation();
  const [downloadOrganismeFichierFile] = useDownloadOrganismeFichierFileMutation();

  const resolveFile = (file: SavedFile, fichierType: OrganismeFichierType) => {
    return (file.id)
      ? downloadOrganismeFichierFile({ id: inscription.organisme!.id, fichierType }).unwrap()
      : fetchLocalFile(file.url!, file.name);
  };

  return {
    isLoading: isInscriptionLoading || isUpdateInscriptionLoading,
    sendInscriptionData: async () => {
      const { organisme, admin1, admin2, representant, delegataire, attestation, delegation } = inscription;

      if (!admin1) {
        navigate('/inscription/step-1');
        throw '';
      }
      if (!representant || !attestation?.type) {
        navigate('/inscription/step-2');
        throw '';
      }
      if (!organisme?.effectifApprenti) {
        navigate('/inscription/step-3');
        throw '';
      }

      const data: OrganismeInscriptionData = {
        siren: organisme.siren!,
        effectifApprenti: organisme.effectifApprenti,
        nda: organisme?.nda || null,
        admin1,
        admin2,
        representant,
        delegataire,

        attestationType: attestation.type,
      };

      const formData = new FormData();

      formData.set('data', jsonToBlob(data));

      const attestationFile: File | null = await resolveFile(attestation, OrganismeFichierType.ATTESTATION_REPRESENTANT);
      if (!attestationFile) {
        appToast.error(t['REPR_MES11']);
        navigate('/inscription/step-2');
        throw '';
      }

      formData.set('attestation', attestationFile);

      if (delegataire) {
        if (!delegation) {
          appToast.error(t['REPR_MES16']);
          navigate('/inscription/step-2');
          throw '';
        }

        const delegationFile: File | null = await resolveFile(delegation, OrganismeFichierType.DELEGATION_POUVOIR);
        if (!delegationFile) {
          appToast.error(t['REPR_MES16']);
          navigate('/inscription/step-2');
          throw '';
        }

        formData.set('delegation', delegationFile);
      }

      if (organisme.id &&
        organisme.statut === OrganismeStatutType.DEMANDE_INFORMATION &&
        // on vérifie que l'organisme à mettre à jour est bien sur la campagne en cours
        organisme.campagne?.statut === CampagneStatut.EN_COURS) {
        return updateInscription({ id: organisme.id, body: formData }).unwrap();
      }

      return createInscription({ body: formData }).unwrap();
    },
  };
}